"use client";

import * as React from "react";

export const MOBILE_BREAKPOINT = 768;
export const TABLET_BREAKPOINT = 1024;

function subscribeToMobileQuery(onStoreChange: () => void): () => void {
  const query = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
  query.addEventListener("change", onStoreChange);
  return () => query.removeEventListener("change", onStoreChange);
}

function readIsMobile(): boolean {
  return window.innerWidth < MOBILE_BREAKPOINT;
}

function readIsMobileOnServer(): undefined {
  return undefined;
}

export function useMobileMatchMedia(): boolean | undefined {
  return React.useSyncExternalStore(
    subscribeToMobileQuery,
    readIsMobile,
    readIsMobileOnServer,
  );
}

function subscribeToMedia(media: string | undefined) {
  return (onStoreChange: () => void): (() => void) => {
    if (!media) return () => {};
    const query = window.matchMedia(media);
    query.addEventListener("change", onStoreChange);
    return () => query.removeEventListener("change", onStoreChange);
  };
}

export function useMediaMatches(media: string | undefined): boolean {
  const subscribe = React.useMemo(() => subscribeToMedia(media), [media]);
  return React.useSyncExternalStore(
    subscribe,
    () => !media || window.matchMedia(media).matches,
    () => false,
  );
}

export function useIsMobile(): boolean {
  return !!useMobileMatchMedia();
}

export function useIsTablet() {
  const [isTablet, setIsTablet] = React.useState<boolean | undefined>(
    undefined,
  );

  React.useEffect(() => {
    const query = `(min-width: ${MOBILE_BREAKPOINT}px) and (max-width: ${TABLET_BREAKPOINT - 1}px)`;
    const mql = window.matchMedia(query);
    const onChange = () => {
      const width = window.innerWidth;
      setIsTablet(width >= MOBILE_BREAKPOINT && width < TABLET_BREAKPOINT);
    };
    mql.addEventListener("change", onChange);
    onChange();
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return !!isTablet;
}

export type Breakpoint = "mobile" | "tablet" | "desktop";

export function useBreakpoint(): Breakpoint {
  const [breakpoint, setBreakpoint] = React.useState<Breakpoint>("desktop");

  React.useEffect(() => {
    const updateBreakpoint = () => {
      const width = window.innerWidth;
      if (width < MOBILE_BREAKPOINT) {
        setBreakpoint("mobile");
      } else if (width < TABLET_BREAKPOINT) {
        setBreakpoint("tablet");
      } else {
        setBreakpoint("desktop");
      }
    };

    const mqlMobile = window.matchMedia(
      `(max-width: ${MOBILE_BREAKPOINT - 1}px)`,
    );
    const mqlTablet = window.matchMedia(
      `(max-width: ${TABLET_BREAKPOINT - 1}px)`,
    );

    mqlMobile.addEventListener("change", updateBreakpoint);
    mqlTablet.addEventListener("change", updateBreakpoint);
    updateBreakpoint();

    return () => {
      mqlMobile.removeEventListener("change", updateBreakpoint);
      mqlTablet.removeEventListener("change", updateBreakpoint);
    };
  }, []);

  return breakpoint;
}
